import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { Text } from '@raptormaps/text';
import { FeatureCard } from './FeatureCard';
import { Icon } from '@raptormaps/icon';
import { useToast } from '@raptormaps/toast';
import { Checkbox } from '@raptormaps/checkbox';
import { Pill } from '@raptormaps/pill';
import { Feature } from '../../api/services/features/features.schema';
import {
  getToggleOrgFeatureMutation,
  enableOrgFeature,
  disableOrgFeature,
} from '../../api/services/features';
import { queryClient } from '../../queryClient';
import {
  SOFTWARE_TEAM_FLAGS,
  VENDOR_FLAGS,
} from '../../constants/featureFlagGroups';
import {
  LoadingContainer,
  PermissionCardGrid,
  PermissionControlsRow,
  FlagSearchWrapper,
  FlagSearchIcon,
  FlagSearchInput,
  FlagSearchClear,
  SelectAllLabel,
  PermissionGroupSection,
  CollapsibleSection,
  CollapsibleHeader,
  CollapsibleHeaderLeft,
  CollapsibleBody,
  ChevronIcon,
} from './OrgDetails.styles';
import theme from '@raptormaps/theme';

interface OrgFeaturePermissionsCardProps {
  orgId: number;
  features: Feature[];
  orgFeatureIds: Set<number>;
  isLoading: boolean;
}

export function OrgFeaturePermissionsCard({
  orgId,
  features,
  orgFeatureIds,
  isLoading,
}: OrgFeaturePermissionsCardProps) {
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const toast = useToast();

  const {
    mutate,
    isLoading: isToggling,
    variables,
  } = useMutation(getToggleOrgFeatureMutation(orgId));

  const handleToggle = (featureId: number, enabled: boolean) => {
    const feature = features.find(f => f.id === featureId);
    const label = feature?.name ?? 'Feature flag';
    mutate(
      { featureId, enabled },
      {
        onSuccess: () =>
          toast.success(
            `${label} ${enabled ? 'enabled' : 'disabled'} successfully.`,
          ),
        onError: () =>
          toast.error(
            `Failed to ${
              enabled ? 'enable' : 'disable'
            } ${label}. Please try again.`,
          ),
      },
    );
  };

  const togglingFeatureIds = new Set(
    isToggling && variables?.featureId != null ? [variables.featureId] : [],
  );

  const query = search.toLowerCase();
  const filteredFeatures = features.filter(f =>
    f.name.toLowerCase().includes(query),
  );

  const softwareTeamFeatures = filteredFeatures.filter(
    f => f.internal_keycode && SOFTWARE_TEAM_FLAGS.has(f.internal_keycode),
  );
  const vendorFeatures = filteredFeatures.filter(
    f => f.internal_keycode && VENDOR_FLAGS.has(f.internal_keycode),
  );
  const productFeatures = filteredFeatures.filter(
    f =>
      !f.internal_keycode ||
      (!SOFTWARE_TEAM_FLAGS.has(f.internal_keycode) &&
        !VENDOR_FLAGS.has(f.internal_keycode)),
  );

  const enabledCount = filteredFeatures.filter(f =>
    orgFeatureIds.has(f.id),
  ).length;
  const allEnabled =
    filteredFeatures.length > 0 && enabledCount === filteredFeatures.length;
  const someEnabled = enabledCount > 0 && !allEnabled;
  const selectAllChecked: boolean | 'indeterminate' = allEnabled
    ? true
    : someEnabled
    ? 'indeterminate'
    : false;

  const handleSelectAll = async () => {
    if (filteredFeatures.length === 0) return;
    const enableAll = !allEnabled;
    const toToggle = filteredFeatures.filter(
      f => orgFeatureIds.has(f.id) !== enableAll,
    );
    if (toToggle.length === 0) return;

    setIsSelectingAll(true);
    try {
      await Promise.all(
        toToggle.map(f =>
          enableAll
            ? enableOrgFeature(orgId, f.id)
            : disableOrgFeature(orgId, f.id),
        ),
      );
      toast.success(
        `All flags ${enableAll ? 'enabled' : 'disabled'} successfully.`,
      );
    } catch {
      toast.error(
        `Failed to ${
          enableAll ? 'enable' : 'disable'
        } all flags. Please try again.`,
      );
    } finally {
      await queryClient.invalidateQueries({
        queryKey: [{ scope: 'features', type: 'org', orgId }],
        exact: false,
      });
      setIsSelectingAll(false);
    }
  };

  const isBusy = isSelectingAll || isToggling;

  return (
    <CollapsibleSection>
      <CollapsibleHeader onClick={() => setOpen(o => !o)}>
        <CollapsibleHeaderLeft>
          <Text variant="paragraph_medium_bold">Product Feature Flags</Text>
        </CollapsibleHeaderLeft>
        <ChevronIcon $open={open}>
          <Icon icon="ChevronDown" size="small" />
        </ChevronIcon>
      </CollapsibleHeader>

      <CollapsibleBody $open={open}>
        {isLoading ? (
          <LoadingContainer>
            <Text variant="paragraph_medium" color={theme.colors.slate_400}>
              Loading...
            </Text>
          </LoadingContainer>
        ) : (
          <>
            <PermissionControlsRow>
              <FlagSearchWrapper>
                <FlagSearchIcon>
                  <Icon icon="MagnifyingGlass" size="small" />
                </FlagSearchIcon>
                <FlagSearchInput
                  placeholder="Search flags..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <FlagSearchClear
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <Icon icon="Xmark" size="small" />
                  </FlagSearchClear>
                )}
              </FlagSearchWrapper>
              <SelectAllLabel>
                <Checkbox
                  size="small"
                  checked={selectAllChecked}
                  disabled={isBusy || filteredFeatures.length === 0}
                  onCheckedChange={handleSelectAll}
                />
                Select all
              </SelectAllLabel>
            </PermissionControlsRow>

            {productFeatures.length > 0 ? (
              <PermissionGroupSection>
                <Pill variant="neutral" size="small">
                  Product
                </Pill>
                <PermissionCardGrid>
                  {productFeatures.map(feature => (
                    <FeatureCard
                      key={feature.id}
                      feature={feature}
                      isEnabled={orgFeatureIds.has(feature.id)}
                      isDisabled={isBusy || togglingFeatureIds.has(feature.id)}
                      onToggle={enabled => handleToggle(feature.id, enabled)}
                    />
                  ))}
                </PermissionCardGrid>
              </PermissionGroupSection>
            ) : null}
            {softwareTeamFeatures.length > 0 ? (
              <PermissionGroupSection>
                <Pill variant="violet" size="small">
                  Software Team
                </Pill>
                <PermissionCardGrid>
                  {softwareTeamFeatures.map(feature => (
                    <FeatureCard
                      key={feature.id}
                      feature={feature}
                      isEnabled={orgFeatureIds.has(feature.id)}
                      isDisabled={isBusy || togglingFeatureIds.has(feature.id)}
                      onToggle={enabled => handleToggle(feature.id, enabled)}
                    />
                  ))}
                </PermissionCardGrid>
              </PermissionGroupSection>
            ) : null}
            {vendorFeatures.length > 0 ? (
              <PermissionGroupSection>
                <Pill variant="orange" size="small">
                  Vendor
                </Pill>
                <PermissionCardGrid>
                  {vendorFeatures.map(feature => (
                    <FeatureCard
                      key={feature.id}
                      feature={feature}
                      isEnabled={orgFeatureIds.has(feature.id)}
                      isDisabled={isBusy || togglingFeatureIds.has(feature.id)}
                      onToggle={enabled => handleToggle(feature.id, enabled)}
                    />
                  ))}
                </PermissionCardGrid>
              </PermissionGroupSection>
            ) : null}
            {filteredFeatures.length === 0 ? (
              <Text variant="paragraph_medium" color={theme.colors.slate_400}>
                No flags match &ldquo;{search}&rdquo;
              </Text>
            ) : null}
          </>
        )}
      </CollapsibleBody>
    </CollapsibleSection>
  );
}
